import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UUID } from "crypto";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { signIn, signOut } from "aws-amplify/auth";
import { ChoiceToggle, ConsentRow, FieldWrapper, PageHeader, SectionHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUserPhone,
  setAddressData,
  setClientDetailsData,
  setClientDetailsId,
  setId_application,
  set_Id_applicationCondition,
  setLegalRepresentative,
  setLoanId,
  setPersonalData,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCurpQuery, useRegisterUserMutation } from "@api/onboarding";
import { useLazyGetSessionInfoQuery } from "@api/session";
import { useLazyGetAddressTypeQuery, useLazyGetPersonTypeQuery, useLazyGetStatesQuery } from "@api/catalogs";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { usePatchCuentaMutation } from "@api/account";
import { usePatchPersonMutation, usePostPersonMutation } from "@api/personasApi";
import { useLazyGetTaxSystemConfigQuery, usePostValidateNextStageMutation } from "@api/applications";
import { CurpQuery, PayloadPersonPatch, PayloadRegisterUser } from "@interfaces/request";
import { Address, StateStructure } from "@interfaces/catalogs";
import { ApiError, StatusCatalog } from "@interfaces/response";
import {
  ADDRESS_CODES,
  ESTADOS_COBERTURA_WUWEI,
  MENSAJE_EDAD_MAXIMA,
  PATHS_COTIZADORES_V2,
  PERSON_CATALOG,
  STATUS_CATALOG,
} from "@helpers/constantes";
import {
  convertSex,
  esEstadoEnCobertura,
  esFechaNacimientoDentroDeEdadMaxima,
  extractRFCFromCurp,
  getFechaNacimientoMinima,
  getNextStatus,
  substractDateFromCURP,
  substractDateFromRFC,
  transformTitleCase,
  validateDates,
} from "@helpers/funciones";
import { CODIGO_POSTAL, CURP, EMAIL, LAST_NAMES, NAME, RFC_PFAE } from "@utils/expreciones";
import { useExistingPersonId } from "@hooks/useExistingPersonId";
import { SinCoberturaWuWei } from "./SinCoberturaWuWei";

const EMPTY_ID = "00000000-0000-0000-0000-000000000000";
const MAX_DATE = dayjs().subtract(18, "years");
const ERROR_INVALID_RFC_DATE = "La fecha de nacimiento del RFC no coincide con la que capturaste";
const ERROR_INVALID_CURP_DATE = "La fecha de nacimiento de la CURP no coincide con la que capturaste";

interface Colonia {
  id: number;
  name: string;
}

type FormValues = {
  name: string;
  first_lastname: string;
  second_lastname: string;
  email: string;
  cp: string;
  calle: string;
  num_ext: string;
  num_int: string;
  colony_id: string;
  otro: string;
  state_id: string;
  state_name: string;
  city_id: string;
  city_name: string;
  locality_id: string;
  locality_name: string;
  date_birth: string;
  birth_state: string;
  sex: string;
  rfc: string;
  curp: string;
  declaration: boolean;
};

const validationSchema = yup.object().shape({
  name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
  first_lastname: yup
    .string()
    .required("Campo requerido")
    .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
  second_lastname: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
  email: yup
    .string()
    .required("Campo requerido")
    .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
  cp: yup.string().required("Campo requerido").matches(CODIGO_POSTAL, "Código Postal inválido"),
  calle: yup.string().required("Campo requerido"),
  num_ext: yup.string().required("Campo requerido"),
  colony_id: yup.string().required("Campo requerido"),
  otro: yup.string().when("colony_id", {
    is: "-1",
    then: (schema) => schema.required("Campo requerido"),
  }),
  state_name: yup.string().required("Campo requerido"),
  locality_id: yup.string().required("Campo requerido"),
  date_birth: yup
    .string()
    .required("Campo requerido")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
    .test("edad-maxima", MENSAJE_EDAD_MAXIMA, esFechaNacimientoDentroDeEdadMaxima)
    .test("is-adult", "Debes ser mayor de 18 años", (value) => !!value && dayjs().diff(dayjs(value), "years") >= 18),
  birth_state: yup.string().required("Campo requerido"),
  sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Selecciona una opción").required("Campo requerido"),
  rfc: yup
    .string()
    .required("Campo requerido")
    .matches(RFC_PFAE, "Formato de RFC no válido: son 13 caracteres con homoclave")
    .test("fecha-nacimiento-rfc", ERROR_INVALID_RFC_DATE, function (value) {
      if (!value) return true;
      return validateDates(this.parent.date_birth, substractDateFromRFC(value));
    }),
  curp: yup
    .string()
    .required("Campo requerido")
    .matches(CURP, "Formato de CURP no válido")
    .test("fecha-nacimiento-curp", ERROR_INVALID_CURP_DATE, function (value) {
      if (!value) return true;
      return validateDates(this.parent.date_birth, substractDateFromCURP(value));
    }),
  declaration: yup.boolean().isTrue(),
});

/** Datos con los que se calcula la CURP; si cambian, se vuelve a consultar. */
const curpKey = ({ name, first_lastname, second_lastname, birth_state, date_birth, sex }: FormValues) =>
  name && first_lastname && birth_state && date_birth && sex
    ? [name, first_lastname, second_lastname, birth_state, date_birth, sex].join("|")
    : "";

/**
 * "Tus datos" de WuWei (`WW_Actividad` del diseño): reemplaza a `RegistroDatosPersonales` para WuWei (PF). Junta
 * en una pantalla el registro del legacy PF (`tofu/pm/RegistroDatosPersonales`), los datos oficiales
 * (`tofu/pm/DatosOficiales`, sin estado civil) y el domicilio particular por CP (`DomicilioAccionista`).
 * Al continuar registra al usuario (o actualiza, si ya existe) y, si el domicilio está fuera de la zona de cobertura
 * (`ESTADOS_COBERTURA_WUWEI`), muestra `SinCoberturaWuWei` sin avanzar de status.
 */
export const RegistroDatosPersonalesWuWei = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const addressData = useSelector((state: RootState) => state.register.address_data);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);

  const [registerUser] = useRegisterUserMutation();
  const [getSessionInfo] = useLazyGetSessionInfoQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getPersonTypes] = useLazyGetPersonTypeQuery();
  const [getStates] = useLazyGetStatesQuery();
  const [getAddressTypes] = useLazyGetAddressTypeQuery();
  const [getCp] = useLazyGetByCpQuery();
  const [getCurp] = useLazyGetCurpQuery();
  const [patchAccount] = usePatchCuentaMutation();
  const [postPerson] = usePostPersonMutation();
  const resolvePersonId = useExistingPersonId();
  const [patchPerson] = usePatchPersonMutation();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [states, setStates] = useState<StateStructure[]>([]);
  const [addressTypeId, setAddressTypeId] = useState("");
  // PF: el cliente es "dueño" y la persona se da de alta como accionista mayoritario y representante legal (legacy).
  const [ownerTypeId, setOwnerTypeId] = useState("");
  const [personTypeIds, setPersonTypeIds] = useState<string[]>([]);
  const [listColonias, setListColonias] = useState<Colonia[]>([]);
  const [cpError, setCpError] = useState("");
  const [isFetchingCp, setIsFetchingCp] = useState(false);
  const [isFetchingCurp, setIsFetchingCurp] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    clientDetails.birth_date ? dayjs(clientDetails.birth_date) : null,
  );
  const [sinCobertura, setSinCobertura] = useState(false);

  const isRegistered = clientDetails.id !== EMPTY_ID;
  const isPhoneAccount = application.account_type === "phone";

  /** `crear_cuenta` necesita los query params de la cotización: igual que `RegistroDatosPersonalesMercader`. */
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(PATHS_COTIZADORES_V2.credito_simple.path);
  };

  const handleSalir = async () => {
    try {
      await signOut();
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "RegistroDatosPersonalesWuWei-signOut" } });
    }
    navigate(PATHS_COTIZADORES_V2.credito_simple.path);
  };

  /**
   * Datos oficiales y domicilio, ya con la solicitud creada: cuenta (`DatosOficiales`), persona y domicilio
   * particular. Igual en el alta y al volver a la pantalla.
   */
  const saveOfficialData = async (loanApplicationId: string, clientDetailsId: string, values: FormValues) => {
    const birth_date = dayjs(values.date_birth).format("YYYY-MM-DD");
    const birth_state = Number(values.birth_state);
    const sex = values.sex === "MASCULINO" ? "MASCULINO" : "FEMENINO";

    await patchAccount({
      accountId: clientDetailsId,
      body: { company_rfc: values.rfc, curp: values.curp, birth_state, birth_date, sex },
    }).unwrap();

    const payloadPerson: PayloadPersonPatch = {
      person_types: personTypeIds,
      name: values.name,
      last_name: values.first_lastname,
      last_name_2: values.second_lastname,
      loan_application_id: loanApplicationId,
      code_phone_number: accountUser.code_phone,
      phone_number: accountUser.phone,
      email: values.email,
      rfc: values.rfc,
      curp: values.curp,
      birth_date,
      birth_state,
      sex: values.sex,
      is_shareholder: true,
    };
    // Si ya existe (volvió a la pantalla, incluso tras iniciar sesión), se actualiza en vez de crear otra.
    const existingPersonId = await resolvePersonId(loanApplicationId);
    const responsePerson = existingPersonId
      ? await patchPerson({ personId: existingPersonId, body: payloadPerson }).unwrap()
      : await postPerson({ body: payloadPerson }).unwrap();
    dispatch(setLegalRepresentative(responsePerson.data));

    const dataAddress = {
      street: values.calle,
      no_ext: values.num_ext,
      no_int: values.num_int,
      colonia_id: Number(values.colony_id) === -1 ? null : +values.colony_id,
      state_id: values.state_id ? +values.state_id : null,
      city_id: values.city_id ? +values.city_id : null,
      locality_id: values.locality_id ? +values.locality_id : null,
      country_id: 1,
      cp: values.cp,
      client_details_id: clientDetailsId,
      person_id: responsePerson.data.id,
      address_type_id: addressTypeId,
      other_neighborhood: values.otro,
    };
    const responseAddress = addressData?.id
      ? await patchAddress({ addressId: addressData.id, data: dataAddress }).unwrap()
      : await postAddress(dataAddress).unwrap();

    dispatch(
      setClientDetailsData({
        rfc: values.rfc,
        curp: values.curp,
        birth_state: values.birth_state,
        birth_date,
        sex: values.sex,
      }),
    );
    dispatch(
      setAddressData({
        id: responseAddress.data.id,
        street: values.calle,
        no_ext: values.num_ext,
        no_int: values.num_int,
        colony_id: +values.colony_id,
        state_id: +values.state_id,
        city_id: +values.city_id,
        locality_id: +values.locality_id,
        postal_code: values.cp,
        state_name: values.state_name,
        city_name: values.city_name,
        locality_name: values.locality_name,
        other_neighborhood: values.otro,
      }),
    );
  };

  /** Alta del usuario y de la solicitud PF, como el legacy; regresa la solicitud y el `status_config` de la sesión. */
  const register = async (values: FormValues): Promise<{ loanApplicationId: string; config: StatusCatalog[] }> => {
    const taxSystem = await getTaxSystemConfig(application.type).unwrap();
    // TODO: confirmar con back el usuario de la cuenta por celular (`account_type: "phone"`).
    const username = isPhoneAccount ? (accountUser.phone_full ?? "") : values.email;
    const fullName = [values.name, values.first_lastname, values.second_lastname].filter(Boolean).join(" ");

    const payloadRegisterUser: PayloadRegisterUser = {
      username,
      email: values.email,
      password: accountUser.password,
      phone: accountUser.phone_full ?? "",
      rol_detail: "",
      rol_id: ownerTypeId,
      loan_application: {
        product_id: application.product_id as UUID,
        tax_systems_id: String(application.tax_system_id),
        source_code: application.source_code,
        financed_services_id: application.financed_services_id,
        guarantee_type_id: application.guarantee_type_id,
        description_guarantee: application.description_guarantee,
        guarantee_types: application.guarantee_types?.length ? application.guarantee_types : null,
        convenio_id: application.convenio_id ?? null,
        payroll_agreement_id: application.convenio_id ?? null,
        leasing_type_id: application.leasing_type_id ?? null,
        quoter_details: application.quoter_details ?? null,
      },
      application_condition: {
        requested_amount: application.loan_amount,
        requested_term: application.loan_term,
        selected_amount: application.loan_amount,
        selected_term: application.loan_term,
        approved_max_term: taxSystem.maxTerm,
        approved_min_term: taxSystem.minTerm,
        loan_amount: application.loan_amount,
        approved_amount: taxSystem.maxAmount,
      },
      client_details: {
        name: values.name,
        last_name: values.first_lastname,
        last_name_2: values.second_lastname,
        person_type_id: ownerTypeId,
        tax_systems_id: String(application.tax_system_id),
        is_previous_client: clientDetails.is_previous_client,
        company_rfc: values.rfc,
        company_name: fullName,
        // PF: el celular de la cuenta se guarda en el cliente.
        country_code: accountUser.code_phone,
        cell_phone_number: accountUser.phone,
      },
    };

    const responseRegister = await registerUser(payloadRegisterUser).unwrap();
    await signIn({ username, password: accountUser.password });
    const session = await getSessionInfo(responseRegister.data.sso_id).unwrap();

    const { loan_application, client_details, application_condition } = responseRegister.data;
    dispatch(
      setAccountUserPhone({
        id: responseRegister.data.id,
        phone_full: accountUser.phone_full ?? "",
        code_phone: accountUser.code_phone ?? "",
        phone: accountUser.phone ?? "",
      }),
    );
    dispatch(setLoanId(loan_application.id));
    dispatch(setClientDetailsId(client_details.id));
    dispatch(setId_application(Number(loan_application.id_application)));
    dispatch(set_Id_applicationCondition(String(application_condition.id)));
    dispatch(
      setPersonalData({
        id: client_details.id,
        name: values.name,
        first_lastname: values.first_lastname,
        second_lastname: values.second_lastname,
        person_type_id: ownerTypeId,
        rol_detail: "",
        cell_phone_verified: false,
        country_code: accountUser.code_phone ?? "",
        phone: accountUser.phone ?? "",
      }),
    );

    await saveOfficialData(loan_application.id, client_details.id, values);
    return { loanApplicationId: loan_application.id, config: session.data.status_config };
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      name: clientDetails.name || "",
      first_lastname: clientDetails.first_lastname || "",
      second_lastname: clientDetails.second_lastname || "",
      email: accountUser.email || "",
      cp: "",
      calle: "",
      num_ext: "",
      num_int: "",
      colony_id: "",
      otro: "",
      state_id: "",
      state_name: "",
      city_id: "",
      city_name: "",
      locality_id: "",
      locality_name: "",
      date_birth: clientDetails.birth_date || "",
      birth_state: clientDetails.birth_state || "",
      sex: clientDetails.sex || "",
      rfc: clientDetails.rfc || "",
      curp: clientDetails.curp || "",
      declaration: false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        setSubmitting(true);

        let config = statusConfig;
        let loanApplicationId = application.id;
        if (isRegistered) {
          await patchAccount({
            accountId: clientDetails.id,
            body: { name: values.name, last_name: values.first_lastname, last_name_2: values.second_lastname },
          }).unwrap();
          await saveOfficialData(application.id, clientDetails.id, values);
          dispatch(
            setPersonalData({
              ...clientDetails,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
            }),
          );
        } else {
          ({ loanApplicationId, config } = await register(values));
        }

        // Fuera de la zona los datos quedan guardados, pero la solicitud no avanza.
        if (!esEstadoEnCobertura(values.state_name, ESTADOS_COBERTURA_WUWEI)) {
          setSinCobertura(true);
          return;
        }

        // Esta pantalla incluye los datos oficiales (CSDOF): se avanza al status que sigue en el `status_config`.
        const nextStatus = getNextStatus(config, STATUS_CATALOG.datos_oficiales.code, []);
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        await triggerPostNextStage({
          applicationId: loanApplicationId,
          statusCode: nextStatus.code,
          username: `TOFU - ${values.email}`,
        }).unwrap();
        navigate(nextStatus.url.startsWith("/") ? nextStatus.url : `/${nextStatus.url}`);
      } catch (error) {
        const api_error = error as ApiError;

        // Mismos códigos que el legacy (`tofu/pm/RegistroDatosPersonales`).
        if (api_error.status && api_error.data) {
          if (api_error.data.code === "101") {
            formik.setFieldError("email", "Correo inválido, dominio no permitido, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "001") {
            formik.setFieldError("email", "El correo ya se encuentra registrado, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "000") {
            formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
            return;
          }
        }

        Sentry.captureException(error, {
          extra: { component: "RegistroDatosPersonalesWuWei-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue, validateForm } = formik;

  const handleCp = useCallback(
    async (value: string) => {
      try {
        setIsFetchingCp(true);
        const { data } = await getCp(value);
        const state = data?.states[0];
        const city = data?.cities[0];
        const locality = data?.localities[0];
        const colonia = data?.colonias[0];

        if (!state || !colonia || !locality) {
          ["state_id", "state_name", "city_id", "city_name", "locality_id", "locality_name", "colony_id"].forEach(
            (field) => setFieldValue(field, ""),
          );
          setCpError("No existe el código postal");
          setListColonias([]);
          return;
        }

        setCpError("");
        setFieldValue("state_id", state.id);
        setFieldValue("state_name", state.name);
        setFieldValue("city_id", city ? city.id : "");
        setFieldValue("city_name", city ? city.name : "");
        setFieldValue("locality_id", locality.id);
        setFieldValue("locality_name", locality.name);
        setFieldValue("colony_id", colonia.id);
        setListColonias(data.colonias);
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el código postal", error);
      } finally {
        setIsFetchingCp(false);
      }
    },
    [getCp, setFieldValue],
  );

  // Catálogos: estados (lugar de nacimiento), tipo de domicilio particular (`CDPA`) y tipos de persona.
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingInitial(true);
        const [responseStates, addressTypes, personTypes] = await Promise.all([
          getStates().unwrap(),
          getAddressTypes().unwrap(),
          getPersonTypes().unwrap(),
        ]);
        setStates(responseStates);
        setAddressTypeId(
          addressTypes.find((address: Address) => address.code === ADDRESS_CODES.particular.code)?.id ?? "",
        );
        const idOf = (code: string) => personTypes.find((item) => item.code === code)?.id;
        setOwnerTypeId(String(idOf(PERSON_CATALOG.dueno_pfae.code) ?? ""));
        setPersonTypeIds(
          [idOf(PERSON_CATALOG.accionista_mayoritario.code), idOf(PERSON_CATALOG.representante_legal.code)]
            .filter(Boolean)
            .map(String),
        );
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "RegistroDatosPersonalesWuWei-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoadingInitial(false);
      }
    };
    fetchInitialData();
  }, [getStates, getAddressTypes, getPersonTypes, dispatch]);

  // Al volver a la pantalla, el domicilio guardado: el CP vuelve a cargar las colonias.
  useEffect(() => {
    if (!addressData?.id) return;
    const prefill = async () => {
      await handleCp(addressData.postal_code);
      setFieldValue("cp", addressData.postal_code);
      setFieldValue("calle", addressData.street);
      setFieldValue("num_ext", addressData.no_ext);
      setFieldValue("num_int", addressData.no_int);
      setFieldValue("colony_id", String(addressData.colony_id));
      setFieldValue("otro", addressData.other_neighborhood ?? "");
    };
    prefill();
    // Solo al entrar con el domicilio del store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData?.id]);

  // CURP y los primeros 10 caracteres del RFC con los datos personales. No se vuelve a consultar si ya venían
  // guardados y no cambiaron.
  const lastCurpKey = useRef(clientDetails.curp ? curpKey(formik.values) : "");
  useEffect(() => {
    const key = curpKey(formik.values);
    if (!key || key === lastCurpKey.current) return;
    lastCurpKey.current = key;

    const { name, first_lastname, second_lastname, birth_state, date_birth, sex } = formik.values;
    const payloadGetCurp: CurpQuery = {
      nombres: name,
      apellido_paterno: first_lastname,
      apellido_materno: second_lastname,
      estado_nacimiento: Number(birth_state).toString().padStart(2, "0"),
      fecha_nacimiento: dayjs(date_birth, "YYYY-MM-DD").format("DD-MM-YYYY"),
      sexo: convertSex(sex),
    };
    setIsFetchingCurp(true);
    getCurp(payloadGetCurp)
      .unwrap()
      .then(async (response) => {
        await setFieldValue("curp", response.data.curp, false);
        await setFieldValue("rfc", extractRFCFromCurp(response.data.curp), false);
        await validateForm();
      })
      .catch((error) => console.error("Hubo un error al obtener la CURP: ", error))
      .finally(() => setIsFetchingCurp(false));
  }, [formik.values, getCurp, setFieldValue, validateForm]);

  const handleUppercase = (field: "rfc" | "curp") => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFieldValue(field, e.target.value.toUpperCase().replace(/\s/g, ""));

  const errorOf = (field: keyof FormValues) => (formik.touched[field] && (formik.errors[field] as string)) || "";
  const disabled = formik.isSubmitting || isLoadingInitial;

  if (sinCobertura) {
    return <SinCoberturaWuWei onCorregirDomicilio={() => setSinCobertura(false)} onSalir={handleSalir} />;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Tus datos"
        title="Cuéntanos de ti"
        subtitle="Con tu domicilio confirmamos que operamos en tu zona; con tus datos oficiales calculamos tu RFC y CURP."
      />

      <Paper variant="lendia_paper">
        <SectionHeader label="¿Cómo te llamas?" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Nombre(s)" error={errorOf("name")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="name"
              value={formik.values.name}
              onChange={(e) => setFieldValue("name", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Nombre(s)"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-nombre-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Apellido paterno" error={errorOf("first_lastname")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="first_lastname"
              value={formik.values.first_lastname}
              onChange={(e) => setFieldValue("first_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido paterno"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-apellido-paterno-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Apellido materno" optionalSuffix="(opcional)" error={errorOf("second_lastname")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="second_lastname"
              value={formik.values.second_lastname}
              onChange={(e) => setFieldValue("second_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido materno"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-apellido-materno-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: "tokens.textTertiary" }}>
          Como aparecen en tu INE.
        </Typography>

        <SectionHeader label="Contacto y domicilio" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper
            label="Correo electrónico"
            full
            hint="Aquí te enviaremos tu oferta y tus contratos."
            error={errorOf("email")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="email"
              type="email"
              value={formik.values.email}
              onChange={(e) => setFieldValue("email", e.target.value.toLowerCase().trim())}
              onBlur={formik.handleBlur}
              placeholder="tu@correo.com"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-correo-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Código Postal" loading={isFetchingCp} error={cpError || errorOf("cp")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="cp"
              value={formik.values.cp}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const cp = e.target.value.replace(/\D/g, "");
                setFieldValue("cp", cp);
                if (cp.length === 5) handleCp(cp);
              }}
              placeholder="00000"
              inputProps={{ maxLength: 5, inputMode: "numeric", "data-testid": "tus-datos-cp-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Calle o avenida" full error={errorOf("calle")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="calle"
              value={formik.values.calle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Calle o avenida"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-calle-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Exterior" error={errorOf("num_ext")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_ext"
              value={formik.values.num_ext}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 42"
              inputProps={{ maxLength: 50, "data-testid": "tus-datos-num-ext-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Interior" optionalSuffix="(opcional)">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_int"
              value={formik.values.num_int}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 3B"
              inputProps={{ maxLength: 50, "data-testid": "tus-datos-num-int-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Colonia" full error={errorOf("colony_id")}>
            {listColonias.length > 0 ? (
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                name="colony_id"
                value={formik.values.colony_id}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (+e.target.value === -1) setFieldValue("otro", "");
                }}
                inputProps={{ "data-testid": "tus-datos-colonia-select" }}
                disabled={disabled}
              >
                <MenuItem value="">Selecciona…</MenuItem>
                {listColonias.map((colonia) => (
                  <MenuItem key={colonia.id} value={colonia.id}>
                    {colonia.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                disabled
                placeholder="Se llenará con el CP"
                inputProps={{ "data-testid": "tus-datos-colonia-input" }}
              />
            )}
          </FieldWrapper>
          {String(formik.values.colony_id) === "-1" && (
            <FieldWrapper label="Otra colonia" full error={errorOf("otro")}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="otro"
                value={formik.values.otro}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 100, "data-testid": "tus-datos-otra-colonia-input" }}
                disabled={disabled}
              />
            </FieldWrapper>
          )}
          <FieldWrapper label="Ciudad">
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.city_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "tus-datos-ciudad-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Municipio / Alcaldía">
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.locality_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "tus-datos-municipio-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Entidad federativa" full>
            <TextField
              size="lendia_input"
              fullWidth
              value={formik.values.state_name}
              placeholder="Se llenará con el CP"
              disabled
              inputProps={{ "data-testid": "tus-datos-estado-input" }}
            />
          </FieldWrapper>
        </Box>

        <SectionHeader label="Datos de nacimiento" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Fecha de nacimiento" error={errorOf("date_birth")}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                  setFieldValue("date_birth", date ? dayjs(date).format("YYYY-MM-DD") : "");
                }}
                minDate={getFechaNacimientoMinima()}
                maxDate={MAX_DATE}
                slotProps={{
                  textField: {
                    size: "lendia_input",
                    fullWidth: true,
                    name: "date_birth",
                    onBlur: formik.handleBlur,
                    inputProps: { "data-testid": "tus-datos-fecha-nacimiento-input" },
                  },
                }}
                disabled={disabled}
              />
            </LocalizationProvider>
          </FieldWrapper>
          <FieldWrapper label="Lugar de nacimiento" error={errorOf("birth_state")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              select
              fullWidth
              name="birth_state"
              SelectProps={{ displayEmpty: true }}
              value={formik.values.birth_state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ "data-testid": "tus-datos-lugar-nacimiento-select" }}
              disabled={disabled}
            >
              <MenuItem value="">Selecciona…</MenuItem>
              {states.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>
          <FieldWrapper label="Sexo" full hint="Como aparece en tu INE." error={errorOf("sex")}>
            <ChoiceToggle
              value={formik.values.sex}
              onChange={(value) => setFieldValue("sex", value)}
              options={[
                { value: "MASCULINO", label: "Masculino" },
                { value: "FEMENINO", label: "Femenino" },
              ]}
              disabled={disabled}
            />
          </FieldWrapper>
        </Box>

        <SectionHeader label="Confirma tu RFC y CURP" />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px 14px" }}>
          <FieldWrapper
            label="RFC con homoclave"
            loading={isFetchingCurp}
            hint="13 caracteres. Calculamos los primeros 10 con tus datos; agrega tu homoclave y corrígelo si no coincide."
            error={errorOf("rfc")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="rfc"
              value={formik.values.rfc}
              onChange={handleUppercase("rfc")}
              onBlur={formik.handleBlur}
              placeholder="HECJ900812K3A"
              inputProps={{ maxLength: 13, "data-testid": "tus-datos-rfc-input" }}
              disabled={disabled || isFetchingCurp}
            />
          </FieldWrapper>
          <FieldWrapper
            label="CURP"
            loading={isFetchingCurp}
            hint="Calculada con tus datos de nacimiento."
            error={errorOf("curp")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="curp"
              value={formik.values.curp}
              onChange={handleUppercase("curp")}
              onBlur={formik.handleBlur}
              placeholder="HECJ900812HDFRRN08"
              inputProps={{ maxLength: 18, "data-testid": "tus-datos-curp-input" }}
              disabled={disabled || isFetchingCurp}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <ConsentRow
          tone="info"
          checked={formik.values.declaration}
          onChange={(checked) => setFieldValue("declaration", checked)}
          disabled={disabled}
          label={
            <>
              <strong>Declaro bajo protesta de decir verdad</strong> que soy el solicitante y que el RFC y CURP son
              correctos.
            </>
          }
        />
      </Box>

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        backLabel="Atrás"
        nextDisabled={!formik.isValid || disabled || !addressTypeId || !ownerTypeId}
        backTestId="tus-datos-back-button"
        nextTestId="tus-datos-continue-button"
      />
    </Box>
  );
};
